import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../../ui/button';
import { fetchWithAuth } from '../../../../utils/api';
import { useNavigation } from '../../../../contexts/NavigationContext';
import type { SimulationCreate, Simulation } from '../../../../types/ombudsman';

interface StartSimulationButtonProps {
  signalId: string;
  variant?: 'default' | 'secondary' | 'outline';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
}

export const StartSimulationButton: React.FC<StartSimulationButtonProps> = ({
  signalId,
  variant = 'default',
  size = 'default',
  className = ''
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateWithReferrer } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStartSimulation = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const payload: SimulationCreate = {
        signal_id: signalId,
      };

      const simulation: Simulation = await fetchWithAuth(
        '/v1/ombudsman-decisions/simulations',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      // Navigate to Case Workspace with simulation ID, preserving referrer
      navigateWithReferrer(`/v3/ombudsman/simulations/${simulation.id}`, location.pathname);
    } catch (err: any) {
      console.error('Failed to start case guidance:', err);
      setError(err.message || 'Failed to start case guidance');
      setIsLoading(false);
    }
  };

  return (
    <div className={className}>
      <Button
        onClick={handleStartSimulation}
        disabled={isLoading}
        variant={variant}
        size={size}
        className="w-full"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            Starting...
          </>
        ) : (
          <>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Start Case Guidance
          </>
        )}
      </Button>
      {error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
};
